import Consents from '@/components/Consents';
import { InputField } from '@/components/form/InputField';
import PasswordField from '@/components/form/PasswordField';
import { Form } from '@/components/primitives/Form';
import { consentsSelector } from '@/features/configuration/configurationSelector';
import { useAuth } from '@/hooks/useAuth';
import usePasswordComplexity from '@/hooks/usePasswordComplexity';
import { useTranslation } from '@/i18n';
import { TransAlert } from '@/i18n/trans/alert';
import { TransText } from '@/i18n/trans/text';
import SignupFormSkeleton from '@/routes/signup/SignupFormSkeleton';
import { useSelector } from '@/store/utils';
import type { ApiErrorResponse } from '@/types/api';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { showErrorToast } from '@/utils/errorToast';
import { createSignupSchema, type SignupValues } from '@/utils/zodSchema';
import { Button, Card, Divider, Icons, Typography } from '@fleet/webshop-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import _pick from 'lodash/pick';
import { type FC, useCallback, useMemo } from 'react';
import { type FieldErrors, useForm } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

interface SignupFormProps {
  email: string;
}

const SignupForm: FC<SignupFormProps> = ({ email }) => {
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const consents = useSelector(consentsSelector);

  const { passwordComplexity, loading: passwordRequirementsLoading } =
    usePasswordComplexity();

  const form = useForm<SignupValues>({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    resolver: zodResolver(createSignupSchema(t, passwordComplexity?.regex)),
    defaultValues: {
      email,
      firstName: '',
      lastName: '',
      phoneNumber: '',
      password: '',
      repeatPassword: '',
      consents:
        consents?.items?.map(({ id, isMandatory, description, code }) => ({
          isMandatory,
          description,
          checked: false,
          id,
          code,
        })) ?? [],
    },
  });
  const { trigger, formState } = form;

  const onSubmit = useCallback(
    async (values: SignupValues) => {
      try {
        const registrationData = {
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          password: values.password!,
          consents: values.consents.map(({ code, checked }) => ({
            code,
            decisionId: checked
              ? 'CONSENT_DECISION.ACCEPTED'
              : 'CONSENT_DECISION.NOT_ACCEPTED',
          })),
          ...(values.phoneNumber && { phoneNumber: values.phoneNumber }),
        };
        await auth.registerWithPassword(registrationData);

        toast.success(<TransText i18nKey="accountCreated" />);
        navigate('/signup/confirmation', {
          replace: true,
          state: { id: values.email },
        });
      } catch (e: unknown) {
        showErrorToast(
          <TransText i18nKey="failedToCreateAccount" />,
          e as ApiErrorResponse
        );
      }
    },
    [auth, navigate]
  );

  const validatePasswordFields = useCallback(() => {
    if (formState.submitCount > 0) {
      trigger(['password', 'repeatPassword']);
    }
  }, [formState.submitCount, trigger]);

  const onInvalid = useCallback((errors: FieldErrors<SignupValues>) => {
    if (Object.keys(_pick(errors, ['firstName', 'lastName', 'email'])).length) {
      toast.error(<TransAlert i18nKey="mandatoryFieldsMustBeFilled" />);
    }
    if (errors.consents) {
      toast.error(<TransAlert i18nKey="mustAgreeWithToC" />);
    }
  }, []);

  const loading = useMemo(
    () => auth.isLoading || passwordRequirementsLoading,
    [auth.isLoading, passwordRequirementsLoading]
  );

  return (
    <div className="mx-auto flex w-full max-w-[410px] flex-col gap-8 p-4 tablet:my-auto laptop:p-0">
      {!loading && (
        <div className="flex items-center gap-4">
          <Typography variant={isLaptopOrBigger ? 'title' : 'mobile-title'}>
            <TransText i18nKey="hello" />
          </Typography>
        </div>
      )}
      {loading ? (
        <SignupFormSkeleton />
      ) : (
        <Card className="rounded-xl">
          <div>
            <Typography variant={isLaptopOrBigger ? 'heading2' : 'subtitle'}>
              <TransText i18nKey="gladToHaveYouOnBoard" />
            </Typography>
            <Typography variant="body1" className="mt-3">
              <TransText i18nKey="fillOutFollowingRegistrationForm" />
            </Typography>
            <Divider className="my-4" />
          </div>
          <Form
            form={form}
            onSubmit={form.handleSubmit(onSubmit, onInvalid)}
            className={cn('flex flex-col gap-3 [&_input]:h-12', {
              '[&_input]:h-8': isLaptopOrBigger,
            })}
          >
            <InputField
              name="email"
              label={<TransText i18nKey="email" />}
              required
              disabled
            />
            <InputField
              name="firstName"
              label={<TransText i18nKey="firstName" />}
              required
            />
            <InputField
              name="lastName"
              label={<TransText i18nKey="lastName" />}
              required
            />
            <InputField
              name="phoneNumber"
              classNames={{ input: 'items-center' }}
              label={<TransText i18nKey="phoneNumber" />}
              type="tel"
            />
            <Divider />
            <div className="flex flex-col gap-3">
              <Typography variant="body1-bold">
                <TransText i18nKey="createAPassword" />
              </Typography>
              {passwordComplexity?.regexLocalization?.description && (
                <Typography variant="body1">
                  {passwordComplexity.regexLocalization.description}
                </Typography>
              )}
            </div>
            <PasswordField
              name="password"
              label={<TransText i18nKey="createNewPassword" />}
              onBlur={validatePasswordFields}
              required
            />
            <PasswordField
              name="repeatPassword"
              label={<TransText i18nKey="repeatNewPassword" />}
              onBlur={validatePasswordFields}
              required
            />
            <Divider />
            <Consents />
            <Button
              type="submit"
              isPending={loading}
              data-testid="signup-form-submit"
              size="medium"
              className="mt-3"
              startAdornment={<Icons.check height={20} width={20} />}
            >
              <TransText i18nKey="confirm" />
            </Button>
          </Form>
        </Card>
      )}
    </div>
  );
};

export default SignupForm;
